import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { settingsAPI } from '../utils/api';

const ContactsContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: #333;
  padding: 80px 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid #dee2e6;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,107,53,0.05) 0%, transparent 70%);
    animation: float 6s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(180deg); }
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #333;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 20px;
  opacity: 0.95;
  line-height: 1.6;
  margin-bottom: 0;
  color: #666;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px;
`;

const ContactsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 80px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfo = styled.div`
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const ContactForm = styled.div`
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin-bottom: 30px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background: #ff6b35;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateX(5px);
  }
`;

const ContactIcon = styled.div`
  width: 50px;
  height: 50px;
  background: #ff6b35;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    fill: white;
  }
`;

const ContactDetails = styled.div`
  flex: 1;
`;

const ContactTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const ContactText = styled.p`
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const ContactLink = styled.a`
  color: #ff6b35;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #e55a2b;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  padding: 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &::placeholder {
    color: #999;
  }
`;

const TextArea = styled.textarea`
  padding: 15px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  background: #ff6b35;
  color: white;
  border: none;
  padding: 18px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 107, 53, 0.4);
    background: #e55a2b;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const MapSection = styled.section`
  background: white;
  padding: 60px 0;
  margin-top: 40px;
`;

const MapContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const MapTitle = styled.h2`
  text-align: center;
  font-size: 36px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
`;

const MapSubtitle = styled.p`
  text-align: center;
  font-size: 18px;
  color: #666;
  margin-bottom: 50px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 400px;
  border: none;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const WorkingHours = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin-top: 20px;
`;

const WorkingTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
`;

const HoursGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HoursItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #e1e5e9;

  &:last-child {
    border-bottom: none;
  }
`;

const Day = styled.span`
  font-weight: 600;
  color: #333;
`;

const Hours = styled.span`
  color: #666;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px;
  border-radius: 8px;
  margin-top: 20px;
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 8px;
  margin-top: 20px;
  border: 1px solid #f5c6cb;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
`;

const Checkbox = styled.input`
  margin-top: 4px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  cursor: pointer;
  
  a {
    color: #ff6b35;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Contacts = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  
  // Значения по умолчанию для контактных данных
  const [contactData, setContactData] = useState({
    legalAddress: '446301, Самарская область, г Отрадный, Спортивная ул, д. 7',
    postalAddress: '446300, Россия, Самарская область, ГО Отрадный, г. Отрадный, Промышленная зона 1',
    inn: '6318073403',
    kpp: '637201001',
    ogrn: '1246300002409',
    contactPhone: '+7 917 120-58-58',
    contactEmail: 'admin@foodstar.pro',
    telegramMain: '@foodstar163',
    telegramSupport: '@foodstar163_support',
    workingHoursWeekdays: '9:00 - 20:00',
    workingHoursWeekend: '10:00 - 18:00'
  });
  
  // Загрузка контактных данных из API
  useEffect(() => {
    const loadContactData = async () => {
      try {
        const response = await settingsAPI.get();
        if (response.data.success && response.data.settings) {
          const settings = response.data.settings;
          const defaults = {
            legalAddress: '446301, Самарская область, г Отрадный, Спортивная ул, д. 7',
            postalAddress: '446300, Россия, Самарская область, ГО Отрадный, г. Отрадный, Промышленная зона 1',
            inn: '6318073403',
            kpp: '637201001',
            ogrn: '1246300002409',
            contactPhone: '+7 917 120-58-58',
            contactEmail: 'admin@foodstar.pro',
            telegramMain: '@foodstar163',
            telegramSupport: '@foodstar163_support',
            workingHoursWeekdays: '9:00 - 20:00',
            workingHoursWeekend: '10:00 - 18:00'
          };
          setContactData({
            legalAddress: settings.legalAddress || defaults.legalAddress,
            postalAddress: settings.postalAddress || defaults.postalAddress,
            inn: settings.inn || defaults.inn,
            kpp: settings.kpp || defaults.kpp,
            ogrn: settings.ogrn || defaults.ogrn,
            contactPhone: settings.contactPhone || defaults.contactPhone,
            contactEmail: settings.contactEmail || defaults.contactEmail,
            telegramMain: settings.telegramMain || defaults.telegramMain,
            telegramSupport: settings.telegramSupport || defaults.telegramSupport,
            workingHoursWeekdays: settings.workingHoursWeekdays || defaults.workingHoursWeekdays,
            workingHoursWeekend: settings.workingHoursWeekend || defaults.workingHoursWeekend
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки контактных данных:', error);
        // Используем значения по умолчанию при ошибке
      }
    };
    
    loadContactData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitError('');

    try {
      // Здесь будет отправка на backend
      console.log('Отправка формы:', formData);
      
      // Имитация отправки
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitMessage('Спасибо за ваше сообщение! Мы свяжемся с вами в ближайшее время.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        consent: false
      });
    } catch (error) {
      setSubmitError('Произошла ошибка при отправке сообщения. Попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactsContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Контакты</HeroTitle>
          <HeroSubtitle>
            Свяжитесь с нами любым удобным способом. Мы всегда готовы помочь 
            вам с продукцией Lagom Life и ответить на ваши вопросы.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <MainContent>
        <ContactsGrid>
          <ContactInfo>
            <SectionTitle>Наши контакты</SectionTitle>
            
            <ContactItem>
              <ContactIcon>
                <svg viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </ContactIcon>
              <ContactDetails>
                <ContactTitle>Юридическая информация</ContactTitle>
                <ContactText>
                  <strong>Юридический адрес:</strong><br/>
                  {contactData.legalAddress}<br/><br/>
                  <strong>Почтовый адрес:</strong><br/>
                  {contactData.postalAddress}<br/><br/>
                  <strong>ИНН:</strong> {contactData.inn}<br/>
                  <strong>КПП:</strong> {contactData.kpp}<br/>
                  <strong>ОГРН:</strong> {contactData.ogrn}
                </ContactText>
              </ContactDetails>
            </ContactItem>

            <ContactItem>
              <ContactIcon>
                <svg viewBox="0 0 24 24">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
              </ContactIcon>
              <ContactDetails>
                <ContactTitle>Телефон</ContactTitle>
                <ContactText>
                  <ContactLink href={`tel:${contactData.contactPhone?.replace(/\s/g, '')}`}>
                    {contactData.contactPhone}
                  </ContactLink> - Контактный телефон
                </ContactText>
              </ContactDetails>
            </ContactItem>

            <ContactItem>
              <ContactIcon>
                <svg viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </ContactIcon>
              <ContactDetails>
                <ContactTitle>Email</ContactTitle>
                <ContactText>
                  <ContactLink href={`mailto:${contactData.contactEmail}`}>
                    {contactData.contactEmail}
                  </ContactLink> - Контактный e-mail
                </ContactText>
              </ContactDetails>
            </ContactItem>

            <ContactItem>
              <ContactIcon>
                <svg viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14.127.104.19.267.18.427l-1.44 6.775c-.039.17-.14.317-.317.317-.208 0-.498-.162-.498-.162s-1.976-1.268-2.64-1.685c-.665-.416-1.346-.859-1.346-.859s-.24-.12-.24-.34c0-.221.24-.34.24-.34s3.47-3.11 3.47-3.11s.237-.118.237-.237c0-.118-.237-.237-.237-.237s-4.09 2.687-4.09 2.687-.322.2-.644.2c-.322 0-.644-.2-.644-.2s-2.153-1.375-2.153-1.375-.257-.162-.257-.324c0-.162.257-.324.257-.324s6.957-2.83 6.957-2.83s.644-.218.966-.218z"/>
                </svg>
              </ContactIcon>
              <ContactDetails>
                <ContactTitle>Telegram</ContactTitle>
                <ContactText>
                  <ContactLink href={`https://t.me/${contactData.telegramMain?.replace('@', '')}`} target="_blank">
                    {contactData.telegramMain}
                  </ContactLink> - Быстрая связь
                </ContactText>
              </ContactDetails>
            </ContactItem>

            <WorkingHours>
              <WorkingTitle>Режим работы</WorkingTitle>
              <HoursGrid>
                <HoursItem>
                  <Day>Пн-Пт</Day>
                  <Hours>{contactData.workingHoursWeekdays}</Hours>
                </HoursItem>
                <HoursItem>
                  <Day>Сб-Вс</Day>
                  <Hours>{contactData.workingHoursWeekend}</Hours>
                </HoursItem>
              </HoursGrid>
            </WorkingHours>
          </ContactInfo>

          <ContactForm>
            <SectionTitle>Напишите нам</SectionTitle>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Ваше имя *</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Введите ваше имя"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Введите ваш email"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Телефон</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+7 (999) 123-45-67"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">Тема сообщения *</Label>
                <Input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="Кратко опишите тему"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Сообщение *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Напишите ваше сообщение..."
                  required
                />
              </FormGroup>

              <CheckboxGroup>
                <Checkbox
                  type="checkbox"
                  id="consent"
                  name="consent"
                  checked={formData.consent}
                  onChange={(e) => setFormData(prev => ({ ...prev, consent: e.target.checked }))}
                  required
                />
                <CheckboxLabel htmlFor="consent">
                  Я даю согласие на обработку моих персональных данных в соответствии с{' '}
                  <a href="/privacy_policy" target="_blank" rel="noopener noreferrer">
                    Политикой конфиденциальности
                  </a>
                </CheckboxLabel>
              </CheckboxGroup>

              <SubmitButton type="submit" disabled={isSubmitting || !formData.consent}>
                {isSubmitting ? 'Отправляем...' : 'Отправить сообщение'}
              </SubmitButton>

              {submitMessage && <SuccessMessage>{submitMessage}</SuccessMessage>}
              {submitError && <ErrorMessage>{submitError}</ErrorMessage>}
            </Form>
          </ContactForm>
        </ContactsGrid>
      </MainContent>

      <MapSection>
        <MapContainer>
          <MapTitle>Как нас найти</MapTitle>
          <MapSubtitle>
            Мы находимся в центре города, рядом с основными транспортными маршрутами. 
            Удобная парковка и легкий доступ на общественном транспорте.
          </MapSubtitle>
          <MapFrame
            src="https://yandex.ru/map-widget/v1/?ll=51.339208,53.358976&z=16&l=map&pt=51.339208,53.358976,pm2rdm"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </MapContainer>
      </MapSection>
    </ContactsContainer>
  );
};

export default Contacts; 